import Button from '@superfunnel/ui-core/v2/Button';
import TextArea from '@superfunnel/ui-core/v2/TextArea';
import { classNames } from '@superfunnel/utils';
import { ArrowRight, ChatBubbleWarning } from 'iconoir-react';
import { ChangeEvent, useState } from 'react';
import {
  InterruptAction,
  InterruptData,
  InterruptResponse,
} from '../../types/agentChat';
import { MarkdownRenderer } from './MarkdownRenderer';

/**
 * Maps semantic intent to Button variant and color props.
 */
function getButtonPropsFromIntent(
  intent: InterruptAction['intent'] = 'neutral',
) {
  switch (intent) {
    case 'primary':
      return { variant: 'default' as const, color: 'primary' as const };
    case 'secondary':
      return { variant: 'outline' as const, color: 'default' as const };
    case 'positive':
      return { variant: 'default' as const, color: 'positive' as const };
    case 'negative':
      return { variant: 'outline' as const, color: 'negative' as const };
    case 'neutral':
    default:
      return { variant: 'ghost' as const, color: 'default' as const };
  }
}

interface AgentInterruptPromptProps {
  interruptData: InterruptData;
  onResume: (response: InterruptResponse) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Component displayed when an agent is interrupted and waiting for user input.
 * Shows the interrupt message and action buttons. If no actions are provided,
 * a default "Resume" button is shown.
 */
export function AgentInterruptPrompt({
  interruptData,
  onResume,
  disabled = false,
  className,
}: AgentInterruptPromptProps) {
  const [freeTextResponse, setFreeTextResponse] = useState('');

  const hasActions = interruptData.actions && interruptData.actions.length > 0;
  const allowFreeText = interruptData.allowFreeText === true; // Only show if explicitly enabled

  const handleActionClick = (actionId: string) => {
    const selectedAction = interruptData.actions?.find(
      (action) => action.id === actionId,
    );

    const response: InterruptResponse = {
      selectedActionId: actionId,
      selectedValue: selectedAction?.value,
      freeTextResponse: freeTextResponse.trim() || undefined,
    };

    onResume(response);
  };

  const handleDefaultResume = () => {
    const response: InterruptResponse = {
      freeTextResponse: freeTextResponse.trim() || undefined,
    };

    onResume(response);
  };

  // Determine if the default resume button should be enabled (when no actions provided)
  const canResume =
    !disabled && (!allowFreeText || freeTextResponse.trim().length > 0);

  // Simple case - no actions, no free text (just a resume button)
  if (!hasActions && !allowFreeText) {
    return (
      <div
        className={classNames(
          'font-inter mt-2 flex min-h-11 items-center justify-between rounded-xl border py-1 pr-2 pl-3',
          className,
        )}
        style={{
          background:
            'linear-gradient(113.22deg, rgba(255, 255, 255, 0.4) 10.7%, #FFFFFF 116.64%)',
          borderImageSource:
            'linear-gradient(95.57deg, rgba(255, 255, 255, 0.3) 10.18%, #FFFFFF 87.97%)',
        }}
      >
        <div className="flex items-center gap-2.5">
          <div className="shrink-0">
            <ChatBubbleWarning className="text-brand-600 h-5 w-5" />
          </div>
          <p className="text-brand-600 text-sm tracking-tight">
            {interruptData.message}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDefaultResume}
          disabled={disabled}
        >
          <span className="text-xs text-[#141C25]">Resume</span>
          <ArrowRight className="size-4 text-[#141C25]" />
        </Button>
      </div>
    );
  }

  // Complex case with actions or free text input
  return (
    <div
      className={classNames('font-inter mt-2 rounded-xl border p-3', className)}
      style={{
        background:
          'linear-gradient(113.22deg, rgba(255, 255, 255, 0.4) 10.7%, #FFFFFF 116.64%)',
        borderImageSource:
          'linear-gradient(95.57deg, rgba(255, 255, 255, 0.3) 10.18%, #FFFFFF 87.97%)',
      }}
    >
      {/* Interrupt Message */}
      <div className="mb-3 flex items-start gap-2.5">
        <div className="mt-0.5 shrink-0">
          <ChatBubbleWarning className="text-brand-600 h-5 w-5" />
        </div>

        <MarkdownRenderer
          content={interruptData.message}
          className="text-brand-600 text-sm tracking-tight"
        />
      </div>

      {/* Free Text Input (if allowed) */}
      {allowFreeText && (
        <div className="mb-3">
          <TextArea
            value={freeTextResponse}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setFreeTextResponse(e.target.value)
            }
            placeholder={
              interruptData.freeTextPlaceholder || 'Type your response...'
            }
            disabled={disabled}
            className="min-h-[60px] resize-none text-sm"
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap justify-end gap-2">
        {hasActions ? (
          interruptData.actions!.map((action) => {
            const buttonProps = getButtonPropsFromIntent(action.intent);
            return (
              <Button
                key={action.id}
                variant={buttonProps.variant}
                color={buttonProps.color}
                size="sm"
                onClick={() => handleActionClick(action.id)}
                disabled={disabled}
              >
                {action.label}
              </Button>
            );
          })
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDefaultResume}
            disabled={!canResume}
          >
            <span className="text-xs text-[#141C25]">Allow</span>
          </Button>
        )}
      </div>
    </div>
  );
}
